import ActionType from '../actionTypes/graphicsLibraryActionType';
import GraphicTags from '../../objects/graphicsLibrary/graphicTags';
import Graphics from '../../objects/graphicsLibrary/graphics';
import { CustomFilterItemValue } from '../../shared/components/filter/types';
import Designs from '../../objects/graphicsLibrary/designs';

export interface GraphicLibraryState {
    loadTags: CommonState & {
        data: GraphicTags | null;
    };
    loadDesigns: CommonState & {
        data: Designs | null;
    };
    loadGraphics: CommonState & {
        data: Graphics | null;
        customFilter: CustomFilterItemValue[] | null;
        allCount: number;
    };
    deleteGraphic: CommonState;
}

interface CommonState {
    failed: boolean;
    loading: boolean;
    success: boolean;
    finished: boolean;
}

interface GraphicTagsAction {
    type: string;
    data: GraphicTags;
}

interface DesignsAction {
    type: string;
    data: Designs;
}

interface DeleteGraphicAction {
    type: string;
    data: Designs;
}

interface GraphicsAction {
    type: string;
    data: Graphics;
    allCount: number;
    customFilter: CustomFilterItemValue[] | null;
}

const commonState = {
    failed: false,
    loading: false,
    success: false,
    finished: false,
};

const stateLoadTags = 'loadTags';
const stateLoadDesigns = 'loadDesigns';
const stateLoadGraphics = 'loadGraphics';
const stateDeleteGraphic = 'deleteGraphic';
type SubState =
    | typeof stateLoadTags
    | typeof stateLoadGraphics
    | typeof stateLoadDesigns
    | typeof stateDeleteGraphic;

const getInitialState = (): GraphicLibraryState => ({
    [stateLoadTags]: {
        ...commonState,
        data: null,
    },
    [stateDeleteGraphic]: {
        ...commonState,
    },
    [stateLoadDesigns]: {
        ...commonState,
        data: null,
    },
    [stateLoadGraphics]: {
        ...commonState,
        data: null,
        customFilter: [
            {
                key: 'myGraphics',
                value: true,
            },
        ],
        allCount: 0,
    },
});

const reset = (): GraphicLibraryState => getInitialState();

const start = (
    subStateName: SubState,
    state: GraphicLibraryState
): GraphicLibraryState => ({
    ...state,
    [subStateName]: {
        ...state[subStateName],
        failed: false,
        loading: true,
        success: false,
        finished: false,
    },
});

const success = (
    subStateName: SubState,
    state: GraphicLibraryState,
    action: GraphicTagsAction | DesignsAction | DeleteGraphicAction
): GraphicLibraryState => ({
    ...state,
    [subStateName]: {
        ...state[subStateName],
        data: action.data,
        failed: false,
        loading: false,
        success: true,
        finished: true,
    },
});

const successWithAllCount = (
    subStateName: SubState,
    state: GraphicLibraryState,
    action: GraphicsAction
): GraphicLibraryState => ({
    ...state,
    [subStateName]: {
        ...state[subStateName],
        data: action.data,
        allCount: action.allCount,
        failed: false,
        loading: false,
        success: true,
        finished: true,
    },
});

const fail = (
    subStateName: SubState,
    state: GraphicLibraryState
): GraphicLibraryState => ({
    ...state,
    [subStateName]: {
        ...state[subStateName],
        failed: true,
        loading: false,
        success: false,
        finished: true,
    },
});

const setGraphicsFilter = (
    subStateName: SubState,
    state: GraphicLibraryState,
    action: GraphicsAction
): GraphicLibraryState => ({
    ...state,
    [subStateName]: {
        ...state[subStateName],
        customFilter: action.customFilter,
    },
});

const resetGraphicsFilter = (
    subStateName: SubState,
    state: GraphicLibraryState
): GraphicLibraryState => ({
    ...state,
    [subStateName]: {
        ...state[subStateName],
        customFilter: null,
    },
});

const reducer = (
    state = getInitialState(),
    action: any
): GraphicLibraryState => {
    switch (action.type) {
        case ActionType.loadTags.start:
            return start(stateLoadTags, state);
        case ActionType.loadTags.success:
            return success(stateLoadTags, state, action);
        case ActionType.loadTags.fail:
            return fail(stateLoadTags, state);
        case ActionType.loadTags.reset:
            return reset();

        case ActionType.loadDesigns.start:
            return start(stateLoadDesigns, state);
        case ActionType.loadDesigns.success:
            return success(stateLoadDesigns, state, action);
        case ActionType.loadDesigns.fail:
            return fail(stateLoadDesigns, state);
        case ActionType.loadDesigns.reset:
            return reset();

        case ActionType.loadGraphics.start:
            return start(stateLoadGraphics, state);
        case ActionType.loadGraphics.success:
            return successWithAllCount(stateLoadGraphics, state, action);
        case ActionType.loadGraphics.fail:
            return fail(stateLoadGraphics, state);
        case ActionType.loadGraphics.reset:
            return reset();

        case ActionType.deleteGraphic.start:
            return start(stateDeleteGraphic, state);
        case ActionType.deleteGraphic.success:
            return success(stateDeleteGraphic, state, action);
        case ActionType.deleteGraphic.fail:
            return fail(stateDeleteGraphic, state);
        case ActionType.deleteGraphic.reset:
            return reset();

        case ActionType.graphicsFilter.set:
            return setGraphicsFilter(stateLoadGraphics, state, action);
        case ActionType.graphicsFilter.clear:
            return resetGraphicsFilter(stateLoadGraphics, state);

        default:
            return state;
    }
};

export default reducer;
